/**
 * The decision half of the "add Respect Football to your home screen" nudge: what
 * platform we are on, whether the sheet is allowed to appear at all, and how a dismissal moves the
 * suppression ladder. Deliberately free of React and of `window` — everything it needs is passed
 * in, so the six-condition gate can be tested as a table rather than by simulating browsers.
 */

/** Same `rwbet:` convention as `THEME_STORAGE_KEY` (src/storage.ts). One record rather than three
 * keys: every read is a single parse behind a single try/catch, and the installed flag and the
 * ladder are only ever read together. */
export const INSTALL_PROMPT_STORAGE_KEY = 'rwbet:pwa-install'

export interface InstallPromptState {
  /** Permanent once true — an installed app must never nudge again, on any device that shares this
   * origin's storage. */
  installed: boolean
  dismissCount: number
  /** Epoch ms of the most recent explicit dismissal, or 0 if there has never been one. */
  lastDismissedAt: number
}

export const INITIAL_INSTALL_PROMPT_STATE: InstallPromptState = {
  installed: false,
  dismissCount: 0,
  lastDismissedAt: 0,
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * How long each dismissal buys, indexed by `dismissCount - 1`: 1st → 7 days, 2nd → 30 days. A third
 * dismissal runs off the end of this list, which is the "never again" rung — three "not now"s is a
 * clear enough answer.
 */
export const SUPPRESSION_WINDOWS_MS = [7 * DAY_MS, 30 * DAY_MS]

/** Delay between the prediction save that triggers the sheet and the sheet arriving, so it doesn't
 * land on top of the save's own confirmation. */
export const INSTALL_PROMPT_DELAY_MS = 1500

/** Structural, matching `src/storage.ts` — this file must type-check without the DOM lib. */
interface MinimalStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function getLocalStorage(): MinimalStorage | undefined {
  return (globalThis as { localStorage?: MinimalStorage }).localStorage
}

/**
 * `null` means "storage is unavailable" — private browsing throws on `localStorage`, and there is
 * no honest way to run a suppression ladder we cannot write to. Callers treat null as *show
 * nothing*: failing closed costs one nudge, failing open nags on every save forever.
 *
 * A missing or corrupt record is a different thing entirely, and returns the initial state.
 */
export function loadInstallPromptState(): InstallPromptState | null {
  const storage = getLocalStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(INSTALL_PROMPT_STORAGE_KEY)
    if (!raw) return INITIAL_INSTALL_PROMPT_STATE
    const parsed = JSON.parse(raw) as Partial<InstallPromptState>
    return {
      installed: parsed.installed === true,
      dismissCount: typeof parsed.dismissCount === 'number' ? parsed.dismissCount : 0,
      lastDismissedAt:
        typeof parsed.lastDismissedAt === 'number' ? parsed.lastDismissedAt : 0,
    }
  } catch {
    return null
  }
}

export function saveInstallPromptState(state: InstallPromptState): void {
  try {
    getLocalStorage()?.setItem(INSTALL_PROMPT_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Nothing to do — the next gate read will fail closed for the same reason.
  }
}

export function recordDismissal(
  state: InstallPromptState,
  now: number
): InstallPromptState {
  return { ...state, dismissCount: state.dismissCount + 1, lastDismissedAt: now }
}

export function markInstalled(state: InstallPromptState): InstallPromptState {
  return { ...state, installed: true }
}

/** True while the most recent dismissal's window is still running, or forever once the ladder is
 * exhausted. */
export function isSuppressed(state: InstallPromptState, now: number): boolean {
  if (state.dismissCount <= 0) return false
  const window = SUPPRESSION_WINDOWS_MS[state.dismissCount - 1]
  if (window === undefined) return true
  return now - state.lastDismissedAt < window
}

/** `unsupported` covers everything with no path to a home screen icon: desktop, in-app browsers,
 * and non-Safari iOS browsers. */
export type InstallPlatform = 'android' | 'ios' | 'unsupported'

/**
 * Browsers embedded in another app (Instagram, Facebook, TikTok, and the generic Android WebView).
 * They cannot add anything to the home screen, so teaching the gesture there is worse than silence.
 */
const IN_APP_BROWSER_MARKERS =
  /\bFBAN\b|\bFBAV\b|\bFB_IAB\b|Instagram|TikTok|Snapchat|LinkedInApp|Line\/|MicroMessenger|Pinterest|; wv\)/i

/** iOS browsers that are not Safari. Each one renders with WebKit, so the UA still says "Safari" —
 * the marker for what is actually driving is the vendor prefix. */
const NON_SAFARI_IOS = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|Brave/i

export interface PlatformHints {
  /** iPadOS 13+ reports itself as "Macintosh"; a touch count is the only thing left to tell the two
   * apart, and it is not in the UA string, so callers pass `navigator.maxTouchPoints`. */
  maxTouchPoints?: number
}

export function detectInstallPlatform(
  userAgent: string,
  { maxTouchPoints = 0 }: PlatformHints = {}
): InstallPlatform {
  if (IN_APP_BROWSER_MARKERS.test(userAgent)) return 'unsupported'
  if (/Android/i.test(userAgent)) return 'android'

  const isIosDevice =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)
  if (!isIosDevice) return 'unsupported'
  if (NON_SAFARI_IOS.test(userAgent)) return 'unsupported'
  return /Safari/i.test(userAgent) ? 'ios' : 'unsupported'
}

export interface InstallGateInput {
  platform: InstallPlatform
  /** Already launched from the home screen — there is nothing to install. */
  isStandalone: boolean
  /** `null` when localStorage is unreachable; see `loadInstallPromptState`. */
  state: InstallPromptState | null
  /** The meaningful action: a prediction has been saved this session. Page views don't count. */
  hasSavedPrediction: boolean
  /** Android only — whether `beforeinstallprompt` has fired and been stashed. Chrome declining to
   * offer installation is the answer; there is no manual-instructions fallback there. */
  hasCapturedPrompt: boolean
  now: number
}

export function canShowInstallPrompt({
  platform,
  isStandalone,
  state,
  hasSavedPrediction,
  hasCapturedPrompt,
  now,
}: InstallGateInput): boolean {
  if (platform === 'unsupported') return false
  if (isStandalone) return false
  if (!state) return false
  if (state.installed) return false
  if (isSuppressed(state, now)) return false
  if (!hasSavedPrediction) return false
  if (platform === 'android' && !hasCapturedPrompt) return false
  return true
}
